/**
 * Node Type Registry
 *
 * Extensible registry of node type definitions that control editing behavior,
 * resize policies, and other type-specific configuration.
 *
 * Each type defines:
 * - Whether it's editable, resizable, and aspect-ratio-locked
 * - How to start/stop editing
 * - How to get intrinsic aspect ratio (for images)
 *
 * The registry provides default behaviors that can be overridden per type.
 */

import type { DirectCanvas } from '@rool/renderer';
import type { InputSystem } from '@rool/input';

// =============================================================================
// Types
// =============================================================================

export interface EditingContext {
	/** The node ID being edited */
	nodeId: string;
	/** The node's DOM element */
	element: HTMLElement;
	/** The DirectCanvas instance (for measuring, invalidation) */
	canvas: DirectCanvas;
	/** The InputSystem (for pushing/popping edit mode) */
	input: InputSystem;
}

export interface EditingResult {
	/** The new content after editing */
	content: string;
	/** Optional measured size (for auto-height nodes) */
	size?: { width: number; height: number };
}

export interface NodeTypeDefinition {
	/** Unique type identifier */
	id: string;

	/** Whether this type supports inline editing */
	editable: boolean;

	/** Whether this type can be resized */
	resizable: boolean;

	/** Whether resize should lock aspect ratio by default */
	lockAspectRatio: boolean;

	/**
	 * CSS selector for the editable element within the node.
	 * Used to find the right element to make editable.
	 */
	editableSelector?: string;

	/**
	 * Start editing a node. Returns a cleanup function that will be called
	 * when editing stops. The cleanup function receives `save: boolean`.
	 *
	 * The returned function should:
	 * 1. Stop editing
	 * 2. Return the editing result (content and optional size)
	 * 3. Clean up any resources (event listeners, editor instances)
	 */
	startEditing?: (ctx: EditingContext) => {
		/** Call to stop editing. Returns the result. */
		stop: (save: boolean) => EditingResult;
		/** Original content (for change detection) */
		originalContent: string;
	};

	/**
	 * Get the intrinsic aspect ratio for this node type (width / height).
	 * Returns undefined to use the node's current dimensions.
	 * Used for proportional resize.
	 */
	getIntrinsicAspectRatio?: (element: HTMLElement) => number | undefined;
}

// =============================================================================
// Registry
// =============================================================================

export interface NodeTypeRegistry {
	/** Register a node type */
	register(definition: NodeTypeDefinition): void;

	/** Get definition for a node type */
	get(typeId: string): NodeTypeDefinition | undefined;

	/** Check if a type is registered */
	has(typeId: string): boolean;

	/** Get all registered types */
	getAll(): NodeTypeDefinition[];

	/** Check if a node type is editable */
	isEditable(typeId: string): boolean;

	/** Check if a node type is resizable */
	isResizable(typeId: string): boolean;

	/** Check if a node type locks aspect ratio */
	isAspectRatioLocked(typeId: string): boolean;
}

export function createNodeTypeRegistry(): NodeTypeRegistry {
	const types = new Map<string, NodeTypeDefinition>();

	return {
		register(definition: NodeTypeDefinition): void {
			types.set(definition.id, definition);
		},

		get(typeId: string): NodeTypeDefinition | undefined {
			return types.get(typeId);
		},

		has(typeId: string): boolean {
			return types.has(typeId);
		},

		getAll(): NodeTypeDefinition[] {
			return Array.from(types.values());
		},

		isEditable(typeId: string): boolean {
			return types.get(typeId)?.editable ?? false;
		},

		isResizable(typeId: string): boolean {
			return types.get(typeId)?.resizable ?? true;
		},

		isAspectRatioLocked(typeId: string): boolean {
			return types.get(typeId)?.lockAspectRatio ?? false;
		},
	};
}

// =============================================================================
// Built-in Type Definitions
// =============================================================================

/** Header node: editable via contentEditable, resizable with dynamic text scaling */
export const HEADER_TYPE: NodeTypeDefinition = {
	id: 'header',
	editable: true,
	resizable: true,
	lockAspectRatio: false,
	editableSelector: '.header-text',
};

/** Sticky note: editable via contentEditable, resizable with dynamic text scaling */
export const STICKY_NOTE_TYPE: NodeTypeDefinition = {
	id: 'sticky-note',
	editable: true,
	resizable: true,
	lockAspectRatio: true,
	editableSelector: '.sticky-note-text',
};

/** Markdown note: editable via Tiptap (app provides startEditing), resizable */
export const MARKDOWN_TYPE: NodeTypeDefinition = {
	id: 'markdown',
	editable: true,
	resizable: true,
	lockAspectRatio: false,
	editableSelector: '.note-editor',
};

/** Image: resize-only, aspect ratio locked */
export const IMAGE_TYPE: NodeTypeDefinition = {
	id: 'image',
	editable: false,
	resizable: true,
	lockAspectRatio: true,
	getIntrinsicAspectRatio: (element: HTMLElement): number | undefined => {
		const img = element.querySelector('img');
		if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
			return img.naturalWidth / img.naturalHeight;
		}
		return undefined;
	},
};

/** File node: not editable, aspect ratio locked */
export const FILE_TYPE: NodeTypeDefinition = {
	id: 'file',
	editable: false,
	resizable: true,
	lockAspectRatio: true,
};

/** Default node types */
export const DEFAULT_NODE_TYPES: NodeTypeDefinition[] = [
	HEADER_TYPE,
	STICKY_NOTE_TYPE,
	MARKDOWN_TYPE,
	IMAGE_TYPE,
	FILE_TYPE,
];
